import React, { useRef, useState, useCallback } from "react";
import cn from "classnames";
import TextInput from "@components/TextInput/TextInput";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import PlusIcon from "@assets/icons/outline/plus.svg?react";
import MinusIcon from "@assets/icons/outline/minus.svg?react";
import styles from './NumberInput.module.scss';

interface NumberInputProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: React.ReactNode;
  disabled?: boolean;
  hasError?: boolean;
  isWide?: boolean;
  wrapSuffix?: boolean;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  suffix,
  disabled,
  hasError,
  isWide,
  wrapSuffix,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showEmptyValue, setShowEmptyValue] = useState(false);

  const handleClickOnContainer = useCallback(() => {
    inputRef.current?.focus && inputRef.current?.focus();
  }, [inputRef.current])

  const handleChange = (rawValue: string) => {
    const v = +rawValue;
    if ((min !== undefined) && (v <= min)) {
      onChange(min);
    } else if ((max !== undefined) && (v >= max)) {
      onChange(max);
    } else {
      onChange(v);
    }
  }

  const increaseValue = useCallback(() => {
    setShowEmptyValue(false)
    handleChange(String(+value + 1));
  }, [value])

  const decreaseValue = useCallback(() => {
    setShowEmptyValue(false)
    handleChange(String(+value - 1));
  }, [value])

  return (
    <div className={styles.container}>
      <IconButton
        icon={MinusIcon}
        onClick={decreaseValue}
        size={IconButtonSize.M}
        type={IconButtonType.ICON}
        disabled={Boolean(disabled || ((min !== undefined) && (+value <= min)))}
      />
      <div
        className={cn(styles.input, isWide && styles.inputWide)}
        onClick={handleClickOnContainer}
      >
        <TextInput
          ref={inputRef}
          type="number"
          inputMode="numeric"
          value={showEmptyValue ? "" : String(value)}
          onChange={(v) => {
            handleChange(v)
            setShowEmptyValue(false)
          }}
          onFocus={() => setShowEmptyValue(!value)}
          onBlur={() => setShowEmptyValue(false)}
          alignValue="center"
          suffix={suffix}
          withWideSuffix={Boolean(suffix)}
          wrapSuffix={wrapSuffix}
          disabled={disabled}
          hasError={hasError}
        />
      </div>
      <IconButton
        icon={PlusIcon}
        onClick={increaseValue}
        size={IconButtonSize.M}
        type={IconButtonType.ICON}
        disabled={Boolean(disabled || ((max !== undefined) && (+value >= max)))}
      />
    </div>
  );
};
